/**
 * APIキー作成完了ページ
 *
 * 作成されたAPIキーのトークンを表示する。
 * トークンはこの画面でのみ表示され、再度確認することはできない。
 */

import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { HabitLayout } from '@/components/habits/HabitLayout'
import { Button } from '@/components/ui/button'
import { settings_api_keys_path } from '@/lib/routes'
import type { PageProps } from '@/types'

interface ApiKey {
  id: number
  name: string
  prefix: string
  last4: string
  createdAt: string
}

interface ApiKeysShowProps extends PageProps {
  apiKey: ApiKey
  token: string
  message: string
}

export default function ApiKeysShow({
  apiKey,
  token,
  message,
}: ApiKeysShowProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // フォールバック: 古いブラウザ用
      const textArea = document.createElement('textarea')
      textArea.value = token
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <>
      <Head title="APIキー作成完了 | Init Habit" />

      <HabitLayout title="APIキー作成完了">
        <div className="mx-auto max-w-xl">
          {/* 成功メッセージ */}
          <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
            <div className="flex items-center gap-2">
              <svg
                className="h-5 w-5 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="font-medium text-green-800 dark:text-green-200">
                APIキーを作成しました
              </span>
            </div>
          </div>

          <div className="bg-card border-border rounded-xl border p-6">
            {/* キー名 */}
            <div className="mb-6">
              <div className="text-muted-foreground mb-1 text-sm">キー名</div>
              <div className="font-medium">{apiKey.name}</div>
            </div>

            {/* トークン */}
            <div className="mb-6">
              <div className="text-muted-foreground mb-2 text-sm">
                APIキー（トークン）
              </div>
              <div className="bg-muted flex items-center gap-2 rounded-lg p-3">
                <code className="flex-1 font-mono text-sm break-all">
                  {token}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="shrink-0"
                >
                  {copied ? 'コピーしました!' : 'コピー'}
                </Button>
              </div>
            </div>

            {/* 警告 */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
              <div className="flex gap-3">
                <svg
                  className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <div className="mb-1 font-medium text-amber-800 dark:text-amber-200">
                    重要: このトークンを安全に保管してください
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {message}
                  </p>
                </div>
              </div>
            </div>

            {/* 使い方 */}
            <div className="mt-6">
              <div className="text-muted-foreground mb-2 text-sm font-medium">
                使い方
              </div>
              <div className="bg-muted rounded-lg p-3">
                <code className="text-sm">
                  Authorization: Bearer {token.slice(0, 10)}...
                </code>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                HTTPリクエストのAuthorizationヘッダーにこのトークンを設定してください
              </p>
            </div>
          </div>

          {/* 完了ボタン */}
          <div className="mt-6 flex justify-center">
            <Link href={settings_api_keys_path()}>
              <Button>APIキー一覧に戻る</Button>
            </Link>
          </div>
        </div>
      </HabitLayout>
    </>
  )
}
